import { useRef, useState } from 'react';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Dialog } from '@optimus-web/core';
import { OptimusProvider } from '@mews-commander/ui';
import { BillNameDialogForm } from './BillNameDialogForm';
import { BillNameFormValues } from './schema';

export interface BillNameDialogProps {
    submitButtonTextKey: Mews.TextKey;
    submitAction: (values: BillNameFormValues) => Promise<void>;
    titleTextKey: Mews.TextKey;
    titleSubstitutes?: Record<string, string>;
    onClose: () => void;
    onCancel?: () => void;
    active: boolean;
    defaultValues?: BillNameFormValues;
}

interface FormRef {
    submit: () => Promise<void>;
}

export const BillNameDialog = ({
    submitButtonTextKey,
    submitAction,
    titleTextKey,
    active,
    onClose,
    titleSubstitutes = {},
    defaultValues,
    onCancel,
}: BillNameDialogProps) => {
    const [loading, setLoading] = useState(false);

    const handleCancel = () => {
        onCancel?.();
        onClose();
    };

    const formRef = useRef<FormRef | null>(null);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            await formRef.current?.submit();
        } finally {
            setLoading(false);
        }
    };

    return (
        <OptimusProvider density="spacious">
            <Dialog
                loading={loading}
                active={active}
                onClose={onClose}
                title={<LocalizedText textKey={titleTextKey} substitutes={titleSubstitutes} />}
                footer={{
                    primary: (
                        <Button variant="primary" onClick={handleSubmit} type="submit">
                            <LocalizedText textKey={submitButtonTextKey} />
                        </Button>
                    ),
                    secondary: (
                        <Button onClick={handleCancel} type="button">
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                    ),
                }}
            >
                <BillNameDialogForm
                    defaultValues={defaultValues}
                    closeDialog={onClose}
                    handleSubmit={submitAction}
                    ref={formRef}
                />
            </Dialog>
        </OptimusProvider>
    );
};
